import React, { useState } from 'react';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import Button from '../button/button';
import BevSafeText from '../bev-safe-text';
import { formatPrice } from '@fshr/shared';

interface ArrivalCardProps {
	date: string;
	time?: string;
	vendor?: string;
	status: string;
	roomType?: { name: string };
	offer?: string;
	averageDailyRate?: string;
	totalRoomRate?: string;
	estimatedTotal?: string;
	disclaimer?: string;
	currencyCode?: string;
	dailyRateDisclaimer?: string;
}

export const ArrivalCard: React.FC<ArrivalCardProps> = ({
	date,
	time,
	vendor,
	status,
	roomType,
	offer,
	averageDailyRate,
	totalRoomRate,
	estimatedTotal,
	disclaimer,
	currencyCode,
	dailyRateDisclaimer,
}) => {
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useState(false);
	const t = useTranslations();

	return (
		<div
			className="flex items-stretch spacing-inline-xs relative cursor-pointer"
			role="listitem"
			aria-label={`${t('Arrival')} - ${date}`}
		>
			<div className="flex flex-col h-full">
				{/* Timeline dot and line */}
				<div
					className="absolute left-[3px] top-0 bottom-0 flex flex-col items-center pt-[2px]"
					aria-hidden={true}
				>
					{/* Dot */}
					<div className="relative flex h-2 w-2">
						<div className="absolute h-2 w-2 rounded-full bg-[rgb(77,77,77,0.45)]" />
					</div>

					{/* Line */}
					<div className="w-[1px] h-full bg-[rgb(77,77,77,0.45)]" />
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 pl-100 pb-200">
				{/* Date */}
				<div className="text-label text-text-secondary mb-080">
					{date} {time && `- ${time}`}
				</div>

				{/* Card */}
				<div
					className="rounded bg-surface-raised-subtle p-200 border border-border-subtle cursor-pointer"
					aria-expanded={isDetailsOpen}
					role="button"
				>
					{/* Title and dropdown icon */}
					<div className="flex justify-between flex-col gap-080">
						<div
							className={`flex justify-between items-center ${isDetailsOpen ? 'pb-150' : ''}`}
							onClick={() => setIsDetailsOpen(!isDetailsOpen)}
						>
							<h3 className="text-subtitle2 text-text-primary">{t('Arrival')}</h3>
							<button
								className="w-6 h-6 flex items-center justify-center"
								aria-label={t(isDetailsOpen ? 'Collapse details' : 'Expand details')}
								tabIndex={0}
							>
								<div
									aria-hidden={true}
									className={cn([
										'pointer-events-none',
										"content=['']",
										'border-[6px]',
										'border-l-transparent',
										'border-r-transparent',
										'border-b-0',
										'border-t-black',
										'top-1/2',
										'-t-y-1/2',
										'ltr:right-[16px]',
										'rtl:left-0',
										isDetailsOpen ? 'rotate-180' : '',
									])}
								/>
							</button>
						</div>
					</div>

					{isDetailsOpen && (
						<div role="region" aria-label={t('Arrival details')}>
							{/* Details Grid */}
							<div className="flex flex-col gap-150 py-150 border-t border-border-subtle">
								{roomType?.name && (
									<div className="flex justify-between">
										<h4 className="text-body3 text-text-primary">{t('Room type')}</h4>
										<p className="text-label text-text-primary">{roomType?.name}</p>
									</div>
								)}
								{offer && (
									<div className="flex justify-between">
										<h4 className="text-body3 text-text-primary">{t('Offer')}</h4>
										<p className="text-label text-text-primary">{offer}</p>
									</div>
								)}
								{averageDailyRate && (
									<div className="flex justify-between">
										<h4 className="text-body3 text-text-primary">{t('Average daily rate')}</h4>
										<div className="text-label flex flex-col items-end">
											<h5 className="text-label text-text-primary">
												{formatPrice({ value: averageDailyRate, currencyCode })}
											</h5>
											<p className="text-caption capitalize text-text-primary">
												<BevSafeText>{dailyRateDisclaimer}</BevSafeText>
											</p>
										</div>
									</div>
								)}
								{totalRoomRate && (
									<div className="flex justify-between">
										<h4 className="text-body3 text-text-primary">{t('Total room rate')}</h4>
										<p className="text-label text-text-primary">
											{formatPrice({ value: totalRoomRate, currencyCode })}
										</p>
									</div>
								)}
								{estimatedTotal && (
									<div className="flex justify-between border-t border-b border-border-subtle py-150">
										<h4 className="text-body3 text-text-primary">{t('Estimated room total')}</h4>
										<p className="text-label text-text-primary">
											{formatPrice({ value: estimatedTotal, currencyCode })}
										</p>
									</div>
								)}
							</div>

							{/* Disclaimer */}
							{disclaimer && (
								<div className="mt-150 text-caption">
									<div
										className={cn('text-text-primary', {
											'line-clamp-3': !isDisclaimerExpanded,
										})}
										dangerouslySetInnerHTML={{ __html: disclaimer }}
										role="note"
									/>
									<Button
										intent="text"
										size="small"
										onClick={() => setIsDisclaimerExpanded(!isDisclaimerExpanded)}
										className="mt-100 text-text-secondary cursor-pointer"
									>
										{t(isDisclaimerExpanded ? 'Show Less' : 'Read More')}
									</Button>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
